/* Public Trust Center — /trust
 *
 * Marketing-grade compliance posture page. No auth required. CISOs evaluating
 * the lab platform land here from sales decks. Data comes from
 * /api/trust/public (5-minute cacheable). Sub-processor list, certification
 * status, and an honest "controls implemented · audit in progress" badge
 * pattern for SOC 2 / ISO 27001.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
    ShieldCheck, Lock, Globe, FileText, AlertCircle, ArrowRight,
    Building2, Mail, Server, CheckCircle2, Clock, Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
    compliant: { dot: "bg-emerald-400", text: "text-emerald-300", label: "Compliant", badge: "bg-emerald-500/10 border-emerald-400/30 text-emerald-200" },
    controls_implemented: { dot: "bg-cyan-400", text: "text-cyan-300", label: "Controls implemented · audit in progress", badge: "bg-cyan-500/10 border-cyan-400/30 text-cyan-200" },
    aligned: { dot: "bg-indigo-400", text: "text-indigo-300", label: "Aligned", badge: "bg-indigo-500/10 border-indigo-400/30 text-indigo-200" },
    in_progress: { dot: "bg-amber-400", text: "text-amber-300", label: "In progress", badge: "bg-amber-500/10 border-amber-400/30 text-amber-200" },
    roadmap: { dot: "bg-violet-400", text: "text-violet-300", label: "Roadmap", badge: "bg-violet-500/10 border-violet-400/30 text-violet-200" },
};

export default function Trust() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        axios.get(`${API}/trust/public`)
            .then((r) => setData(r.data))
            .catch((e) => setErr(e?.message || "Failed to load"));
    }, []);

    if (err) {
        return (
            <div className="min-h-screen bg-[#0B1120] text-white grid place-items-center px-5">
                <div className="text-center">
                    <AlertCircle size={32} className="mx-auto text-rose-400" />
                    <div className="mt-3 text-[15px]">Trust Center temporarily unavailable</div>
                    <div className="mt-1 text-[12px] text-white/50">{err}</div>
                </div>
            </div>
        );
    }
    if (!data) {
        return (
            <div className="min-h-screen bg-[#0B1120] text-white grid place-items-center">
                <Loader2 size={28} className="animate-spin text-cyan-400" />
            </div>
        );
    }

    return (
        <div data-testid="trust-public-page" className="bg-[#0B1120] text-white">
            {/* HERO */}
            <section className="relative tr-grain overflow-hidden">
                <div className="absolute -top-40 -right-20 w-[640px] h-[640px] tr-glow bg-cyan-500/20" />
                <div className="absolute -bottom-40 -left-20 w-[480px] h-[480px] tr-glow bg-emerald-500/15" />
                <div className="relative max-w-[1280px] mx-auto px-5 lg:px-8 pt-28 pb-24 lg:pt-32 lg:pb-28">
                    <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-cyan-400 mb-4">· Trust Center · v {new Date(data.last_updated).toISOString().slice(0, 10)}</div>
                    <h1 className="font-display font-light text-5xl lg:text-7xl tracking-tight leading-[0.98]">
                        Your patients&rsquo; data,<br />
                        <span className="italic text-cyan-300/90 font-extralight">audited &amp; encrypted.</span>
                    </h1>
                    <p className="mt-6 max-w-[620px] text-[15.5px] text-white/65 leading-[1.75]">
                        Toothmatrix is built for clinical labs that take security seriously. We publish our
                        compliance posture, sub-processor list, encryption attestation, and a 1-click
                        CISO-grade audit pack — generated live from production.
                    </p>
                    <div className="mt-9 flex flex-wrap gap-3">
                        <a href="mailto:security@toothrocket.com" data-testid="trust-security-contact" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-cyan-400 text-[#022c33] hover:bg-cyan-300 text-[14px] font-semibold transition">
                            <Mail size={15} /> security@toothrocket.com
                        </a>
                        <Link to="/login" data-testid="trust-login-cta" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/15 hover:border-cyan-400/50 hover:bg-white/5 text-[14px] font-medium transition">
                            <ShieldCheck size={15} /> Sign in for audit pack <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* COMPANY */}
            <section className="border-t border-white/5">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-16 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4">
                        <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-cyan-400">· Company</div>
                        <h2 className="mt-3 font-display text-3xl tracking-tight">Who you&rsquo;re trusting</h2>
                    </div>
                    <div className="lg:col-span-8 grid sm:grid-cols-2 gap-6 text-[14px]">
                        <CompanyRow icon={Building2} label="Legal entity">{data.company.legal_name}</CompanyRow>
                        <CompanyRow icon={Globe} label="Headquarters">{data.company.headquarters}</CompanyRow>
                        <CompanyRow icon={Mail} label="Data Protection Officer"><a className="text-cyan-300 hover:text-cyan-200" href={`mailto:${data.company.data_protection_officer}`}>{data.company.data_protection_officer}</a></CompanyRow>
                        <CompanyRow icon={ShieldCheck} label="Security contact"><a className="text-cyan-300 hover:text-cyan-200" href={`mailto:${data.company.security_contact}`}>{data.company.security_contact}</a></CompanyRow>
                        <CompanyRow icon={Clock} label="Uptime target">{data.uptime_target}</CompanyRow>
                        <CompanyRow icon={AlertCircle} label="Bug bounty">{data.company.bug_bounty}</CompanyRow>
                    </div>
                </div>
            </section>

            {/* FRAMEWORKS */}
            <section className="border-t border-white/5 bg-white/[0.02]">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-20">
                    <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-cyan-400 mb-3">· Section 02 · compliance frameworks</div>
                    <h2 className="font-display font-light text-4xl lg:text-5xl tracking-tight">Where we stand.</h2>
                    <p className="mt-3 max-w-[640px] text-[14.5px] text-white/55">
                        We mark each framework honestly: <em>compliant</em>, <em>controls implemented</em>, or <em>aligned</em>.
                        We never claim a certification we don&rsquo;t hold.
                    </p>
                    <div className="mt-10 grid lg:grid-cols-2 gap-4" data-testid="trust-frameworks">
                        {data.frameworks.map((fw) => {
                            const s = STATUS_STYLES[fw.status] || STATUS_STYLES.in_progress;
                            return (
                                <div key={fw.id} data-testid={`trust-framework-${fw.id}`} className="rounded-2xl border border-white/10 bg-[#0F1A2E]/60 p-6 hover:border-cyan-400/30 transition">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-display text-2xl tracking-tight">{fw.name}</div>
                                            <div className="text-[12.5px] text-white/45 mt-1">{fw.full_name}</div>
                                        </div>
                                        <span className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10.5px] font-mono uppercase tracking-wider ${s.badge}`}>
                                            <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
                                            {s.label}
                                        </span>
                                    </div>
                                    {fw.target_audit && (
                                        <div className="mt-3 text-[12px] text-cyan-300/80 font-mono-tr">Target audit · {fw.target_audit}</div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* SUB-PROCESSORS */}
            <section className="border-t border-white/5">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-20">
                    <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-cyan-400 mb-3">· Section 03 · sub-processors</div>
                    <h2 className="font-display font-light text-4xl lg:text-5xl tracking-tight">The vendors we use.</h2>
                    <p className="mt-3 max-w-[640px] text-[14.5px] text-white/55">
                        Updated quarterly. We give 30 days&rsquo; written notice before adding a new sub-processor.
                    </p>
                    <div className="mt-10 overflow-x-auto rounded-2xl border border-white/10 bg-[#0F1A2E]/60">
                        <table className="w-full text-[13.5px]" data-testid="trust-subprocessors">
                            <thead className="bg-white/5 text-[10.5px] uppercase tracking-wider text-white/50">
                                <tr>
                                    <th className="text-start px-5 py-3 font-mono-tr">Vendor</th>
                                    <th className="text-start px-4 py-3 font-mono-tr">Purpose</th>
                                    <th className="text-start px-4 py-3 font-mono-tr">Data classes</th>
                                    <th className="text-start px-4 py-3 font-mono-tr">Location</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {data.subprocessors.map((sp) => (
                                    <tr key={sp.name} data-testid={`trust-sp-${sp.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`} className="hover:bg-white/[0.02]">
                                        <td className="px-5 py-3.5">
                                            <a href={sp.url} target="_blank" rel="noreferrer noopener" className="font-medium text-white hover:text-cyan-300">{sp.name}</a>
                                        </td>
                                        <td className="px-4 py-3.5 text-white/70">{sp.purpose}</td>
                                        <td className="px-4 py-3.5 text-white/55">{sp.data_classes.join(", ")}</td>
                                        <td className="px-4 py-3.5 text-white/55">{sp.location}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* PROMISE */}
            <section className="border-t border-white/5 bg-gradient-to-b from-transparent to-cyan-950/20">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-20 grid lg:grid-cols-3 gap-6">
                    <Promise icon={Lock} title="Encryption">TLS 1.2+ in transit · AES-256 at rest · HSTS preloaded · bcrypt-12 for credentials.</Promise>
                    <Promise icon={Server} title="Tenant isolation">Every database query is row-level filtered by <span className="font-mono-tr text-cyan-300/80">tenant_id</span>. Cross-tenant reads are physically blocked at the data layer.</Promise>
                    <Promise icon={FileText} title="Tamper-evident audit log">Every administrative action is hash-chained (SHA-256). Modifying any row invalidates every entry that follows it.</Promise>
                </div>
            </section>

            {/* CTA — get the audit pack */}
            <section className="border-t border-white/5">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-20 text-center">
                    <CheckCircle2 size={32} className="mx-auto text-cyan-400" />
                    <h2 className="mt-5 font-display font-light text-4xl lg:text-5xl tracking-tight">
                        Need the full audit pack?
                    </h2>
                    <p className="mt-4 max-w-[560px] mx-auto text-[15px] text-white/60">
                        Tenant-admins can download a CISO-grade PDF with hash-chain verification, SBOM,
                        access-control review, and encryption attestation — one click from your admin
                        console.
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <Link to="/login" data-testid="trust-cta-signin" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cyan-400 text-[#022c33] hover:bg-cyan-300 text-[14px] font-semibold transition">
                            Sign in to download <ArrowRight size={15} />
                        </Link>
                        <a
                            href={`${API}/trust/dpa.pdf`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="trust-cta-dpa"
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-cyan-400/40 hover:border-cyan-400 hover:bg-cyan-400/5 text-cyan-200 text-[14px] font-medium transition"
                        >
                            <FileText size={14} /> Preview DPA template
                        </a>
                        <a href="mailto:security@toothrocket.com" data-testid="trust-cta-request" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 hover:border-cyan-400/50 hover:bg-white/5 text-[14px] font-medium transition">
                            Request as a buyer <Mail size={14} />
                        </a>
                    </div>
                </div>
            </section>

            <div className="text-center text-[11px] font-mono-tr text-white/30 py-10 border-t border-white/5">
                Last updated · {new Date(data.last_updated).toISOString().slice(0, 19).replace("T", " ")} UTC
            </div>
        </div>
    );
}

function CompanyRow({ icon: Icon, label, children }) {
    return (
        <div className="flex items-start gap-3">
            <Icon size={16} className="text-cyan-400 mt-0.5 shrink-0" />
            <div>
                <div className="text-[10.5px] font-mono-tr tracking-[0.18em] uppercase text-white/40">{label}</div>
                <div className="text-white/85 mt-0.5">{children}</div>
            </div>
        </div>
    );
}

function Promise({ icon: Icon, title, children }) {
    return (
        <div className="rounded-2xl border border-white/10 bg-[#0F1A2E]/40 p-6">
            <Icon size={20} className="text-cyan-400" />
            <div className="font-display text-xl tracking-tight mt-4">{title}</div>
            <p className="text-[13.5px] text-white/60 mt-2 leading-relaxed">{children}</p>
        </div>
    );
}
